import { Routes } from '@angular/router';
import { Login } from './pages/login/login';
import { ForgotPassword } from './pages/forgot-password/forgot-password';
import { Layout } from './layout/layout';
import { Dashboard } from './pages/dashboard/dashboard';
import { PartnerDashboard } from './pages/partner-dashboard/partner-dashboard';
import { PartnerRecords } from './pages/partner-dashboard/partner-records';
import { MemberLookup } from './pages/partner-dashboard/member-lookup';
import { MenuMaster } from './pages/menu-master/menu-master';
import { PermissionPage } from './pages/permission/permission';
import { Partners } from './pages/partners/partners';
import { PartnerDetail } from './pages/partners/partner-detail';
import { Notifications } from './pages/notifications/notifications';
import { CronJobs } from './pages/cron-jobs/cron-jobs';
import { SeriesTypes } from './pages/series-types/series-types';
import { SeriesMaster } from './pages/series-master/series-master';
import { SeriesFormat } from './pages/series-format/series-format';
import { Forbidden } from './pages/forbidden/forbidden';
import { ChangePassword } from './pages/change-password/change-password';
import { ProductMasterPage } from './pages/product-master/product-master';
import { AdminUsersPage } from './pages/admin-users/admin-users';
import { ZonesPage } from './pages/zones/zones';
import { StatesPage } from './pages/states/states';
import { RegionalOfficesPage } from './pages/regional-offices/regional-offices';
import { PartnerPremiumAccessPage } from './pages/partner-premium-access/partner-premium-access';
import { PartnerKycPage } from './pages/partner-kyc/partner-kyc';
import { PartnerKycReviewPage } from './pages/partner-kyc-review/partner-kyc-review';
import { KycPendingPage } from './pages/kyc-pending/kyc-pending';
import { MessagingPage } from './pages/messaging/messaging';
import { MessageCatalogPage } from './pages/message-catalog/message-catalog';
import { AdminNotificationsPage } from './pages/admin-notifications/admin-notifications';
import { AppNotificationsPage } from './pages/app-notifications/app-notifications';
import { CommunicationConfigPage } from './pages/communication-config/communication-config';
import { MessageTemplatesPage } from './pages/message-templates/message-templates';
import { MemberForm } from './pages/member/member-form';
import { PaymentGatewaysPage } from './pages/payments/gateways/payment-gateways';
import { PaymentIntegrationsPage } from './pages/payments/integrations/payment-integrations';
import { PaymentTypesPage } from './pages/payments/types/payment-types';
import { CollectPaymentPage } from './pages/payments/collect/collect-payment';
import { PaymentsListPage } from './pages/payments/list/payments-list';
import { PaymentDetailPage } from './pages/payments/detail/payment-detail';
import { ReceiptFinder } from './pages/payments/receipt-finder/receipt-finder';
import { PaymentSuccess } from './pages/payments/payment-success/payment-success';
import { PaymentResult } from './pages/payments/payment-result/payment-result';
import { CoiFinder } from './pages/policies/coi-finder/coi-finder';
import { CoiPage } from './pages/policies/coi-page/coi-page';
import { GlobalMasterPage } from './pages/masters/global-master';
import { ReceiptSettingsPage } from './pages/masters/receipt-settings';
import { CoiHeaderPage } from './pages/masters/coi-header';
import { HospicashSettingPage } from './pages/masters/hospicash-setting';
import { TestModePage } from './pages/masters/test-mode';
import { GoLivePage } from './pages/masters/go-live';
import { Clients } from './pages/clients/clients';
import { ClientDetail } from './pages/clients/client-detail';
import { ClientMessagingPage } from './pages/client-messaging/client-messaging';
import { ClientCallbacksPage } from './pages/client-callbacks/client-callbacks';
import { AdminDashboardPage } from './pages/admin-dashboard/admin-dashboard';
import { ReportSchedulerPage } from './pages/report-scheduler/report-scheduler';
import { MembersPage } from './pages/members/members';
import { authGuard } from './guards/auth-guard';
import { kycGuard } from './guards/kyc-guard';
import { dashboardRedirectGuard } from './guards/dashboard-redirect';
export const routes: Routes = [
  { path: '', redirectTo: 'login', pathMatch: 'full' },
  { path: 'login', component: Login },
  { path: 'forgot-password', component: ForgotPassword },
  // PUBLIC, no-login customer enrollment + payment page (talks only to the Client API's /api/enroll BFF via
  // a one-time code). A top-level sibling of /login, so the authGuard (which only guards Layout children)
  // never runs. Lazy-loaded + client-rendered (see app.routes.server.ts).
  { path: 'enroll', loadComponent: () => import('./pages/enroll/enroll.component').then((m) => m.EnrollComponent) },
  {
    path: '',
    component: Layout,
    // authGuard checks login + menu access; kycGuard parks non-approved partners on /partner-kyc.
    canActivate: [authGuard, kycGuard],
    children: [
      // '/dashboard' only DISPATCHES by role (admin → /admin/dashboard, partner →
      // /partner-dashboard); the demo Dashboard renders only during SSR, pre-redirect.
      { path: 'dashboard', component: Dashboard, canActivate: [dashboardRedirectGuard] },
      // Admin-only, all-partners/all-clients analytics (backend [Authorize(Roles="Admin")]).
      { path: 'admin/dashboard', component: AdminDashboardPage },
      // Admin-only recurring COI/payment report schedules (backend [Authorize(Roles="Admin")]).
      { path: 'admin/report-scheduler', component: ReportSchedulerPage },
      // Admin-only login-account management: standalone admin logins, password resets, enable/disable.
      { path: 'admin-users', component: AdminUsersPage },
      // Admin-only master list of ALL insured members (backend AdminDashboard/Members).
      { path: 'members', component: MembersPage },
      // Terms & Conditions admin CRUD. LAZY-loaded (loadComponent) so the Quill rich-text
      // editor never enters the SSR/main bundle; client-rendered (see app.routes.server.ts).
      {
        path: 'terms-conditions',
        loadComponent: () =>
          import('./pages/terms-conditions/terms-conditions').then((m) => m.TermsConditionsPage),
      },
      { path: 'partner-dashboard', component: PartnerDashboard },
      { path: 'partner-records', component: PartnerRecords },
      { path: 'member-lookup', component: MemberLookup },
      { path: 'menu-master', component: MenuMaster },
      { path: 'permissions', component: PermissionPage },
      { path: 'partners', component: Partners },
      { path: 'partners/:id', component: PartnerDetail },
      { path: 'notifications', component: Notifications },
      { path: 'cron-jobs', component: CronJobs },
      { path: 'series-types', component: SeriesTypes },
      { path: 'series-master', component: SeriesMaster },
      { path: 'series-format', component: SeriesFormat },
      // Product Master — master policies + premium rules + benefit breakup on ONE page.
      // The two old routes redirect here so existing Menu Master rows keep working.
      { path: 'product-master', component: ProductMasterPage },
      { path: 'master-policies', redirectTo: 'product-master' },
      // Client-side hierarchy masters: Zone / State / Regional Office (RO -> master policy).
      { path: 'zones', component: ZonesPage },
      { path: 'states', component: StatesPage },
      { path: 'regional-offices', component: RegionalOfficesPage },
      { path: 'premium-rules', redirectTo: 'product-master' },
      { path: 'partner-premium-access', component: PartnerPremiumAccessPage },
      { path: 'partner-kyc', component: PartnerKycPage },
      { path: 'partner-kyc-review', component: PartnerKycReviewPage },
      { path: 'kyc-pending', component: KycPendingPage },
      // One tabbed module: Rules (channel assignment) | Templates | Providers | Outbox.
      { path: 'messaging', component: MessagingPage },
      // Absorbed into /messaging — redirect so existing bookmarks and menu rows keep working.
      { path: 'client-messaging', redirectTo: 'messaging?tab=rules', pathMatch: 'full' },
      { path: 'message-templates', redirectTo: 'messaging?tab=templates', pathMatch: 'full' },
      { path: 'communication-config', redirectTo: 'messaging?tab=providers', pathMatch: 'full' },
      { path: 'message-catalog', component: MessageCatalogPage },
      { path: 'admin-notifications', component: AdminNotificationsPage },
      // Partner mobile-app pushes (one partner or broadcast) + partner-reported issue threads (admin).
      { path: 'app-notifications', component: AppNotificationsPage },
      { path: 'member-onboarding', component: MemberForm },
      // Payments module (admin masters + member payment flow).
      { path: 'payments/gateways', component: PaymentGatewaysPage },
      { path: 'payments/integrations', component: PaymentIntegrationsPage },
      { path: 'payments/types', component: PaymentTypesPage },
      { path: 'payments/collect/:memberId', component: CollectPaymentPage },
      { path: 'payments/list', component: PaymentsListPage },
      // Single-payment details (any status). Scoped server-side (admin → any; partner → own).
      { path: 'payments/detail/:id', component: PaymentDetailPage },
      // Receipts — finder (search by receipt no / payment id) + the success page (gateway redirect target).
      { path: 'payments/receipts', component: ReceiptFinder },
      { path: 'payments/success/:paymentId', component: PaymentSuccess },
      // Where a REDIRECT gateway (Generali) returns the customer. Reads ?ref= and confirms
      // server-side — the ?status= in the URL is only a hint.
      { path: 'payments/result', component: PaymentResult },
      // Certificate of Insurance — finder (by policy / member) + the per-member certificate page.
      { path: 'policies/certificates', component: CoiFinder },
      { path: 'policies/certificate/:memberId', component: CoiPage },
      // Global Master — data types + their values (admin master-detail).
      { path: 'masters/global', component: GlobalMasterPage },
      // Receipt Settings — issuer block (Main / Partner + per-client) printed on payment receipts (admin).
      { path: 'masters/receipt-settings', component: ReceiptSettingsPage },
      // COI Header — issuer/header block (Main / Partner + per-client) printed on certificates (admin).
      { path: 'masters/coi-header', component: CoiHeaderPage },
      // Hospicash Settings — company letterhead/contacts + images for the partner welcome letter (admin).
      { path: 'masters/hospicash-setting', component: HospicashSettingPage },
      { path: 'masters/test-mode', component: TestModePage },
      { path: 'masters/go-live', component: GoLivePage },
      // Client Management module (admin masters + per-client detail tabs).
      { path: 'clients', component: Clients },
      { path: 'clients/:id', component: ClientDetail },
      { path: 'client-callbacks', component: ClientCallbacksPage },
      { path: 'forbidden', component: Forbidden },
      { path: 'change-password', component: ChangePassword },
      // Future authenticated pages go here and share the sidebar/topbar shell.
    ],
  },
];
